import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from './AuthContext';
import { Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faBriefcase, faHeart, faKey, faSave, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import './Profile.css';

const Profile = () => {
    const { auth, userProfile, updateProfile, updatePassword, logout } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [passwordData, setPasswordData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [profileData, setProfileData] = useState({
        name: '',
        email: '',
        interests: '',
        business_name: ''
    });
    const [activeTab, setActiveTab] = useState('profile');

    // Load user profile data when component mounts
    useEffect(() => {
        if (userProfile) {
            setProfileData({
                name: userProfile.name || '',
                email: userProfile.email || '',
                interests: userProfile.interests || '',
                business_name: userProfile.business_name || ''
            });
        }
    }, [userProfile]);

    if (!auth) {
        return <Navigate to="/login" />;
    }

    if (!userProfile) {
        return (
            <div className="profile-container">
                <div className="loading-spinner"></div>
                <p>Loading profile...</p>
            </div>
        );
    }

    const handleProfileChange = (e) => {
        setProfileData({
            ...profileData,
            [e.target.name]: e.target.value
        });
    };

    const handlePasswordChange = (e) => {
        setPasswordData({
            ...passwordData,
            [e.target.name]: e.target.value
        });
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ text: '', type: '' });

        try {
            const { success, error } = await updateProfile(profileData);
            
            if (success) {
                setMessage({ text: 'Profile updated successfully!', type: 'success' });
            } else {
                setMessage({ text: error.message || 'Failed to update profile.', type: 'error' });
            }
        } catch (err) {
            setMessage({ text: 'An unexpected error occurred.', type: 'error' });
            console.error('Profile update error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        
        // Validate passwords
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setMessage({ text: 'Passwords do not match.', type: 'error' });
            return;
        }
        
        if (passwordData.newPassword.length < 6) {
            setMessage({ text: 'Password must be at least 6 characters long.', type: 'error' });
            return;
        }
        
        setLoading(true);
        setMessage({ text: '', type: '' });

        try {
            const { success, error } = await updatePassword(passwordData.newPassword);
            
            if (success) {
                setMessage({ text: 'Password updated successfully! Please log in again.', type: 'success' });
                setPasswordData({ newPassword: '', confirmPassword: '' });
                
                // Log the user out after password change
                setTimeout(() => {
                    logout();
                }, 3000);
            } else {
                setMessage({ text: error.message || 'Failed to update password.', type: 'error' });
            }
        } catch (err) {
            setMessage({ text: 'An unexpected error occurred.', type: 'error' });
            console.error('Password update error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="profile-container">
            <div className="profile-header">
                <h1>My Account</h1>
                <div className="user-type-badge">
                    {userProfile.userType === 'buyer' ? 'Buyer' : 'Seller'}
                </div>
            </div>
            
            {message.text && (
                <div className={`message ${message.type}`}>
                    {message.text}
                </div>
            )}
            
            <div className="profile-tabs">
                <button 
                    className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
                    onClick={() => setActiveTab('profile')}
                >
                    <FontAwesomeIcon icon={faUser} /> Profile
                </button>
                <button 
                    className={`tab-button ${activeTab === 'security' ? 'active' : ''}`}
                    onClick={() => setActiveTab('security')}
                >
                    <FontAwesomeIcon icon={faKey} /> Security
                </button>
            </div>
            
            <div className="profile-content">
                {activeTab === 'profile' && (
                    <>
                        <h2 className="section-title">Personal Information</h2>
                        <form onSubmit={handleProfileSubmit} className="profile-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>
                                        <FontAwesomeIcon icon={faUser} /> Name
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={profileData.name}
                                        onChange={handleProfileChange}
                                        disabled={loading}
                                        required
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>
                                        <FontAwesomeIcon icon={faEnvelope} /> Email
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={profileData.email}
                                        onChange={handleProfileChange}
                                        disabled={true}
                                        required
                                    />
                                    <small>Email cannot be changed</small>
                                </div>
                            </div>
                            
                            {userProfile.userType === 'buyer' ? (
                                <div className="form-group">
                                    <label>
                                        <FontAwesomeIcon icon={faHeart} /> Interests
                                    </label>
                                    <input
                                        type="text"
                                        name="interests"
                                        value={profileData.interests}
                                        onChange={handleProfileChange}
                                        disabled={loading}
                                        placeholder="e.g., cars, boats, sneakers"
                                    />
                                </div>
                            ) : (
                                <div className="form-group">
                                    <label>
                                        <FontAwesomeIcon icon={faBriefcase} /> Business Name
                                    </label>
                                    <input
                                        type="text"
                                        name="business_name"
                                        value={profileData.business_name}
                                        onChange={handleProfileChange}
                                        disabled={loading}
                                        required
                                    />
                                </div>
                            )}
                            
                            <button 
                                type="submit" 
                                className="submit-button"
                                disabled={loading}
                            >
                                <FontAwesomeIcon icon={faSave} /> {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </form>
                    </>
                )}
                
                {activeTab === 'security' && (
                    <>
                        <h2 className="section-title">Change Password</h2>
                        <form onSubmit={handlePasswordSubmit} className="password-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>New Password</label>
                                    <input
                                        type="password"
                                        name="newPassword"
                                        value={passwordData.newPassword}
                                        onChange={handlePasswordChange}
                                        disabled={loading}
                                        required
                                        minLength={6}
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>Confirm New Password</label>
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        value={passwordData.confirmPassword}
                                        onChange={handlePasswordChange}
                                        disabled={loading}
                                        required
                                        minLength={6}
                                    />
                                </div>
                            </div>
                            
                            <button 
                                type="submit" 
                                className="submit-button"
                                disabled={loading}
                            >
                                <FontAwesomeIcon icon={faSave} /> {loading ? 'Updating...' : 'Update Password'}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

export default Profile; 